import React, { useMemo, useState } from "react";
import {
  Box,
  Divider,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import { OVERVIEW_LLM_LABELS } from "../../utils/constant";
import { transformDataForMultibarChart } from "../../utils/helpers";

// ── Color palette (mirrors existing AttributeBars palette) ──────────────────
const COLOR_POOL = [
  "#C7F0EC", "#FF6F6F", "#CEB4FF", "#FFCE6E",
  "#92B7FE", "#FEE7E6", "#B0E57C", "#FFB6C1",
  "#ADD8E6", "#D8BFD8", "#FFD700", "#7FFFD4",
];

// CA-local color map so it doesn't interfere with AttributeBars' module-level map
const _caColorMap = {};
let _caColorIdx = 0;
function getBrandColor(brand) {
  if (!_caColorMap[brand]) {
    _caColorMap[brand] = COLOR_POOL[_caColorIdx % COLOR_POOL.length];
    _caColorIdx++;
  }
  return _caColorMap[brand];
}

// ── Consensus badge ────────────────────────────────────────────────────────
// ── Bar hover tooltip ─────────────────────────────────────────────────────
const TOOLTIP_SX = {
  bgcolor: "#1e2340",
  fontSize: "0.73rem",
  borderRadius: "10px",
  padding: "8px 12px",
  boxShadow: "0 6px 20px rgba(0,0,0,0.28)",
  "& .MuiTooltip-arrow": { color: "#1e2340" },
};

function BarTooltip({ brandName, compositeScore, perLLM }) {
  return (
    <div style={{ minWidth: 160 }}>
      <div style={{ fontWeight: 700, fontSize: "0.76rem", marginBottom: 4 }}>
        {brandName}
      </div>
      <div style={{ fontSize: "0.71rem", marginBottom: 5, opacity: 0.85 }}>
        Composite: <strong>{Math.round(compositeScore)}</strong>
      </div>
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.15)", paddingTop: 5 }}>
        {perLLM.map(({ llm, value }) => (
          <div key={llm} style={{ fontSize: "0.69rem", marginBottom: 2, display: "flex", justifyContent: "space-between", gap: 12 }}>
            <span style={{ color: "#a8c4f0" }}>{OVERVIEW_LLM_LABELS[llm] || llm}</span>
            <span style={{ opacity: 0.85 }}>
              {value != null ? value : <em style={{ opacity: 0.5 }}>not mentioned</em>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Attribute panel (one side: either Positive or Negative) ────────────────
const INITIAL_COUNT  = 10;
const LOAD_MORE_STEP = 10;

function CAAttributePanel({ attributes, perLLMLookup }) {
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  const brandKeys = useMemo(() => {
    if (!attributes?.length) return [];
    const set = new Set();
    attributes.forEach(attr =>
      Object.keys(attr).filter(k => k !== "name").forEach(k => set.add(k))
    );
    return [...set];
  }, [attributes]);

  const maxValue = useMemo(() => {
    if (!attributes?.length || !brandKeys.length) return 1;
    return Math.max(
      ...attributes.flatMap(attr => brandKeys.map(b => Number(attr[b]) || 0)),
      1
    );
  }, [attributes, brandKeys]);

  const visible   = attributes.slice(0, visibleCount);
  const hasMore   = attributes.length > visibleCount;

  if (!attributes.length) {
    return <Typography color="text.secondary" variant="body2">Data Not Available</Typography>;
  }

  return (
    <>
      {/* Brand legend */}
      {brandKeys.length > 1 && (
        <Box display="flex" mb={2} flexWrap="wrap" gap={2}>
          {brandKeys.map(brand => (
            <Box key={brand} display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: getBrandColor(brand), flexShrink: 0 }} />
              <Typography variant="body2">{brand}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Grid container spacing={2}>
        {visible.map((attr, i) => {
          const attrNorm   = attr.name?.toLowerCase?.().trim() ?? "";
          const barsToShow = brandKeys.filter(b => attr[b] > 0);

          return (
            <React.Fragment key={i}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="flex-start" gap={2}>
                  {/* Attribute name */}
                  <Box flex={1} display="flex" alignItems="center" pt={0.5}>
                    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                      {attr.name}
                    </Typography>
                  </Box>

                  {/* Horizontal bars — one per brand */}
                  <Box display="flex" flexDirection="column" alignItems="flex-start" sx={{ mt: 0.5, mb: 0.5 }}>
                    {barsToShow.map(brand => {
                      const compositeScore = Number(attr[brand]) || 0;
                      const barWidth       = (compositeScore / maxValue) * 450;
                      const lookup         = perLLMLookup[attrNorm]?.[brand] || {};
                      const perLLM         = lookup.perLLM || [];

                      return (
                        <Tooltip
                          key={brand}
                          title={
                            <BarTooltip
                              brandName={brand}
                              compositeScore={compositeScore}
                              perLLM={perLLM}
                            />
                          }
                          arrow
                          placement="top"
                          componentsProps={{ tooltip: { sx: TOOLTIP_SX } }}
                        >
                          <Box sx={{ width: 400, display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                            <Box sx={{
                              height: 12,
                              width: Math.max(barWidth, 4),
                              backgroundColor: getBrandColor(brand),
                              borderRadius: 0.5,
                              cursor: "pointer",
                              transition: "opacity 0.15s",
                              "&:hover": { opacity: 0.8 },
                            }} />
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
              </Grid>
            </React.Fragment>
          );
        })}
      </Grid>

      {hasMore && (
        <Box sx={{ textAlign: "center", mt: 1 }}>
          <button
            onClick={() => setVisibleCount(c => c + LOAD_MORE_STEP)}
            style={{ border: "none", background: "none", color: "#1976d2", cursor: "pointer", fontSize: "0.85rem" }}
          >
            Load More
          </button>
        </Box>
      )}
    </>
  );
}

// ── Main composite component ───────────────────────────────────────────────

export default function CAPositiveNegativeComposite({
  category,
  market,
  persona,
  multiLLMs,
  filterBrands,
  ctx,
}) {
  const llms       = (multiLLMs || []).filter(Boolean);
  const totalLLMs  = llms.length;

  if (!totalLLMs || !filterBrands?.length) return null;

  // ── Step 1: Collect per-attribute, per-brand data from all LLMs ──────────
  // Structure: { attrNorm → { displayName, brands: { brandName → { totalValue, perLLM, llmCount } } } }
  const posData = {};
  const negData = {};

  // Track which LLMs mentioned each attribute (for consensus badge)

  llms.forEach(llm => {
    const brandList = ctx.positive_negative_attributes?.[llm]?.[category]?.[market]?.[persona];
    if (!Array.isArray(brandList)) return;

    filterBrands.forEach(brandName => {
      const brandEntry = brandList.find(b => b.name === brandName);
      if (!brandEntry) return;

      const accumulate = (attrs, dataMap) => {
        (attrs || []).forEach(attr => {
          const norm = attr.name.toLowerCase().trim();
          if (!dataMap[norm]) dataMap[norm] = { displayName: attr.name, brands: {} };
          if (!dataMap[norm].brands[brandName]) {
            dataMap[norm].brands[brandName] = { totalValue: 0, perLLM: [], llmCount: 0 };
          }
          dataMap[norm].brands[brandName].totalValue += attr.value;
          dataMap[norm].brands[brandName].perLLM.push({ llm, value: attr.value });
          dataMap[norm].brands[brandName].llmCount  += 1;
        });
      };

      accumulate(brandEntry.positives, posData);
      accumulate(brandEntry.negatives, negData);
    });
  });

  // ── Step 2: Build chart input for transformDataForMultibarChart ───────────
  // format: { brandName: { attrDisplayName: compositeScore } }
  // compositeScore = sum(values from all LLMs) / totalLLMs  (missing LLMs contribute 0)
  const buildChartInput = (dataMap) => {
    const input = {};
    Object.entries(dataMap).forEach(([, attrData]) => {
      Object.entries(attrData.brands).forEach(([brandName, brandData]) => {
        const compositeScore = brandData.totalValue / totalLLMs;
        if (compositeScore > 0) {
          if (!input[brandName]) input[brandName] = {};
          input[brandName][attrData.displayName] = compositeScore;
        }
      });
    });
    return input;
  };

  // ── Step 3: Build per-LLM lookup for tooltip (attrNorm → brand → perLLM) ─
  // Includes ALL selected LLMs — shows "not mentioned" for LLMs that didn't surface it
  const buildPerLLMLookup = (dataMap) => {
    const lookup = {};
    Object.entries(dataMap).forEach(([norm, attrData]) => {
      lookup[norm] = {};
      Object.entries(attrData.brands).forEach(([brandName, brandData]) => {
        lookup[norm][brandName] = {
          perLLM: llms.map(llm => {
            const found = brandData.perLLM.find(p => p.llm === llm);
            return { llm, value: found ? found.value : null };
          }),
          llmCount: brandData.llmCount,
        };
      });
    });
    return lookup;
  };

  const posChartData = transformDataForMultibarChart(buildChartInput(posData), true) || [];
  const negChartData = transformDataForMultibarChart(buildChartInput(negData), true) || [];

  const posPerLLMLookup = buildPerLLMLookup(posData);
  const negPerLLMLookup = buildPerLLMLookup(negData);

  if (!posChartData.length && !negChartData.length) return null;

  return (
    <div className="col-md-12 mt-4">
      <div className="card p-3 custCard">
        <div className="row">
          <div className="col-md-12">

            <Box sx={{ mx: "auto" }}>
              <Grid container spacing={6}>

                {/* Positive Attributes */}
                <Grid item xs={6}>
                  <Typography variant="h6" align="left" gutterBottom>
                    Positive Attributes
                  </Typography>
                  <hr />
                  <CAAttributePanel
                    attributes={posChartData}
                    perLLMLookup={posPerLLMLookup}
                  />
                </Grid>

                {/* Negative Attributes */}
                <Grid item xs={6}>
                  <Typography variant="h6" align="left" gutterBottom>
                    Negative Attributes
                  </Typography>
                  <hr />
                  <CAAttributePanel
                    attributes={negChartData}
                    perLLMLookup={negPerLLMLookup}
                  />
                </Grid>

              </Grid>
            </Box>

          </div>
        </div>
      </div>
    </div>
  );
}
